export const TEST_STATUS_PENDING = 'pending';
export const TEST_STATUS_PASSED = 'passed';
export const TEST_STATUS_FAILED = 'failed';
export const TEST_STATUS_AUTO_MERGED = 'auto_merged';
export const TEST_STATUS_FLAGGED_FOR_REVIEW = 'flagged_for_review';

export const ENVIRONMENT_STATE_SCHEMA_VERSION = 1;

export interface ReplayChannelSnapshot {
    id: string;
    channelDef_id?: string;
    organisation_id?: string;
    instance_id?: string;
    name?: string;
    enabled?: boolean;
    activated?: boolean;
    agentLink?: string;
    agentSecret?: string;
    options: Record<string, unknown>;
}

export interface ReplayChannelEntityDefSnapshot {
    id: string;
    channelDef_id?: string;
    entityDef_id?: string;
    key?: string;
    name?: unknown;
    [key: string]: unknown;
}

export interface ReplayActionInputDefSnapshot {
    id: string;
    actionDef_id?: string;
    key?: string;
    fieldDef_id?: string;
    fieldEntityDef_id?: string;
    type?: string;
    multiValueStyle?: string;
    [key: string]: unknown;
}

export interface ReplayActionDefSnapshot {
    id: string;
    channelDef_id?: string;
    /** Channel definitions whose allActionDefs relation exposed this action during capture. */
    visibleThroughChannelDefIds?: string[];
    sourceDef_id?: string;
    operation?: string;
    useForDatiQuery?: boolean;
    name?: unknown;
    actionInputDefs?: ReplayActionInputDefSnapshot[];
    [key: string]: unknown;
}

export interface ReplayFlowScriptDefSnapshot {
    id: string;
    flowDef_id?: string;
    key: string;
    script?: string;
    testScript?: string;
    resolutionOrder?: number;
    [key: string]: unknown;
}

export interface ReplayFetchPayload {
    id: string;
    actionDef_id?: string;
    channel_id?: string;
    sourceDef_id?: string;
    flowStepDef_id?: string;
    name?: string;
    method: string;
    url?: string;
    request?: unknown;
    data?: unknown;
    data2?: unknown;
    success?: boolean;
    error?: string | null;
    ignoredError?: boolean;
    responseHeaders?: Record<string, string>;
}

export interface ReplayFetchPayloadSidecar {
    fetch_id: string;
    ordinal: number;
    payload: ReplayFetchPayload;
    byteLength?: number;
    sha256?: string;
}

export interface ReplayChannelError {
    id: string;
    channel_id?: string;
    flow_id?: string | null;
    entityDefKey?: string;
    externalId?: string;
    displayName?: string;
    hasError?: boolean;
    ignoredError?: boolean;
    ignoreForFuture?: boolean;
    lastError?: string | null;
    transferCount?: number;
    errorCount?: number;
    lastErrorTransferAt?: string | null;
    lastTransferAt?: string | null;
    firstTransferAt?: string | null;
    resolvableBy?: string | null;
    objectDate?: string | null;
    objectValue?: number | null;
    channelErrorMessage?: string | null;
    channelErrorField?: string | null;
    type?: string | null;
}

export interface ReplayEventDetector {
    id: string;
    channel_id?: string;
    organisation_id?: string;
    channelDef_id?: string;
    sourceDef_id?: string;
    content?: Record<string, unknown>;
}

export interface ReplayCustomData {
    id: string;
    organisation_id?: string;
    channel_id?: string;
    flow_id?: string;
    organisation_channel_flow?: string;
    key?: string;
    content?: Record<string, unknown>;
    type?: string;
    title?: string;
    updatedAt?: string;
    createdAt?: string;
}

export interface ReplayMutexBeforeImage {
    organisation_id?: string;
    flow_id?: string;
    key: string;
    exists: boolean;
    row?: Record<string, unknown>;
}

export interface ReplayClipboardBeforeImage {
    organisation_id?: string;
    flow_id?: string;
    key: string;
    exists: boolean;
    content?: unknown;
}

export interface ReplaySelectedChannelMetadata {
    alias?: string;
    channelId: string;
    channelDef_id?: string;
    name?: string;
}

export interface ReplaySelectedChannelCompatibility {
    alias: string;
    channelId: string;
    channelDef_id?: string;
}

export interface ReplayScriptFingerprint {
    scope: 'flow';
    ownerId?: string;
    id?: string;
    key: string;
    hash: string;
    length: number;
}

export interface ReplayCompatibilitySnapshot {
    schemaVersion: 1;
    selectedChannels?: ReplaySelectedChannelCompatibility[];
    scriptFingerprints?: ReplayScriptFingerprint[];
}

export interface ReplayRequestEnvelope {
    originalRequest?: string;
    originalHeaders?: string;
    originalUrl?: string;
    originalRequestUrl?: string;
    flowIncomingEmail_id?: string;
    replayRequestCreatedAt?: string;
    event?: Record<string, unknown>;
}

export interface ReplayOrganisationSnapshot {
    id: string;
    name?: string;
    billingName?: string;
    adminExtraEmail?: string;
    email?: string;
    locale?: string;
    instance_id?: string;
}

export interface ReplayUserSnapshot {
    id: string;
    email?: string;
    otherEmails?: string;
    firstname?: string;
    surname?: string;
    nickname?: string;
    locale?: string;
    globalPermissions?: unknown;
    featureFlags?: unknown;
}

export interface ReplayMetadata {
    sourceFlowInvocation_id?: string;
    organisation_id?: string;
    instance_id?: string;
    flow_id?: string;
    flowDef_id?: string;
    flowDefVersion_id?: string;
    startFlowStepDef_id?: string;
    startType?: string;
    /** Source-context capture time at the actual execution boundary. */
    sourceContextCapturedAt?: string | Date;
    /** Legacy queue/invocation row creation time, retained as a fallback. */
    flowInvocationCreatedAt?: string | Date;
    globalApiUrl?: string;
    serviceUrl?: string;
    formUrl?: string;
    serviceHookProxyUrl?: string;
    fetchPayloadStorage?: 'embedded' | 'db' | 'testcase-fetch-table';
    fetchPayloadOmittedReason?: string;
    fetchPayloadCount?: number;
    fetchPayloadByteLength?: number;
    fetchPayloadSha256?: string;
    fetchPayloadIntegrityScope?: 'embedded-storage-v1' | 'sidecar-row-manifest-v1';
    fetchPayloadManifestSha256?: string;
    embeddedEnvironmentStateSizeBytes?: number;
    compactedEnvironmentStateSizeBytes?: number;
    flowOptionsCaptureMode?: 'lossless-v1';
}

export interface ReplayEventStartLocator {
    eventId: string;
    organisationId: string;
    channelId: string;
    sourceDefId: string;
    createdAt?: string;
}

export interface ReplayEnvironmentState {
    flowState: Record<string, unknown>;
    preRunFlowState?: Record<string, unknown>;
    flowOptions: Record<string, unknown>;
    preRunFlowOptions?: Record<string, unknown>;
    channels: Record<string, ReplayChannelSnapshot>;
    channelEntityDefs?: ReplayChannelEntityDefSnapshot[];
    actionDefs?: ReplayActionDefSnapshot[];
    flowScriptDefs?: ReplayFlowScriptDefSnapshot[];
    fetches: ReplayFetchPayload[];
    channelErrors?: ReplayChannelError[];
    eventDetectors?: ReplayEventDetector[];
    customDatas?: ReplayCustomData[];
    customDataBeforeImages?: ReplayCustomData[];
    customDataAbsentBeforeImages?: ReplayCustomData[];
    mutexes?: ReplayMutexBeforeImage[];
    clipboards?: ReplayClipboardBeforeImage[];
    /** Legacy compatibility only. New source-context capture does not persist generic transmissions. */
    initialTransmission?: Record<string, unknown>;
    eventStart?: ReplayEventStartLocator;
    selectedChannels: Record<string, ReplaySelectedChannelMetadata>;
    compatibility?: ReplayCompatibilitySnapshot;
    organisation?: ReplayOrganisationSnapshot;
    user?: ReplayUserSnapshot;
    request?: ReplayRequestEnvelope;
    metadata?: ReplayMetadata;
}

/**
 * Captured environment state at the time of test creation.
 * This frozen state is injected during test replay to ensure deterministic results.
 */
export interface EnvironmentState {
    schemaVersion?: number;
    /** Flow state (persistent key-value storage like lastSyncDate, pagination cursors) */
    flowState: Record<string, unknown>;
    /** Channel options (configuration like base URLs, custom settings) */
    channelOptions: Record<string, unknown>;
    /** Flow options (user-configured parameters like target folder ID) */
    flowOptions: Record<string, unknown>;
    replay?: ReplayEnvironmentState;
    /** Timestamp when this state was captured (used to freeze the clock during replay) */
    capturedAt?: number;
}

export interface SnapshotChannelWrite {
    method: string;
    url: string;
    body: any;
    headers: any;
    responseBody: any;
    stepId?: string;
    actionName?: string;
    fetchName?: string;
    '@_actionName'?: string;
    '@_fetchName'?: string;
    '@_writeKeyBase'?: string;
    '@_writeKey'?: string;
}

export interface SnapshotOutput {
    finished: boolean;
    failed: boolean;
    error?: string | null;
    status: any;
    channelWrites: SnapshotChannelWrite[];
    executedSteps?: string[];
}

export interface TestInvocation {
    id: string;
    realId?: string;
    organisation_id: string;
    instance_id?: string;
    sourceFlowInvocation_id?: string;
    flow_id?: string;
    flowDef_id: string;
    flowDefVersion_id?: string;
    flowStepDef_id?: string;
    startFlowStepDef_id?: string;
    startType?: string;
    name: string;
    fetchIds: string[]; // JSON array in DB
    expectedOutput: SnapshotOutput; // JSON in DB
    expectedOutputPreview?: string;
    environmentState?: EnvironmentState | string | null; // JSON in DB - frozen state for deterministic replay
    __replayFetchPayloads?: ReplayFetchPayloadSidecar[]; // transient DAO input for oversized replay fetch sidecar rows
    lastRunAt?: Date;
    lastStatus?: string;
    lastError?: string;
    lastDuration?: number;
    version: number; // Optimistic concurrency control version
    createdAt: Date;
    updatedAt: Date;
    author_id?: string;
    lastSuccessfulRun?: TestRun;
    lastUnsuccessfulRun?: TestRun;
    originalRequest?: string;
    originalHeaders?: string;
    originalUrl?: string;
    flowIncomingEmail_id?: string;
    originalRequestUrl?: string;
    runs?: { edges: { node: TestRun }[] };
}

export interface TestRun {
    id: string;
    realId?: string;
    testInvocation_id: string;
    organisation_id: string;
    status: string;
    error?: string;
    actualOutput?: SnapshotOutput; // JSON
    computedDiff?: any;
    duration?: number;
    createdAt: Date;
    expiresIn?: number;
    lastHeartbeatAt?: Date;
}
